import React from "react";
import styles from "./Table.scss";
import classNames from "classnames";
import { IColumn } from "./Table";

interface TableBodyProps<T, K extends keyof T> {
  data: T[];
  columns: IColumn<T, K>[];
  extra?: (row: T) => React.ReactNode;
  action?: (row: T, rowIndex: number) => React.ReactNode;
  hasFooter: boolean;
  hasAction: boolean;
  title?: string;
  className?: string;
  rowClassName?: (row: T) => string | undefined;
  onRowClick?: (row: T) => void;
}

export const TableBody = <T, K extends keyof T>({
  data,
  columns,
  extra,
  action,
  hasFooter,
  hasAction,
  title,
  className,
  rowClassName,
  onRowClick,
}: TableBodyProps<T, K>) => {
  const isClickable = onRowClick != null;

  return (
    <tbody className={classNames(className, { [styles.hasFooter]: hasFooter })}>
      {title && (
        <tr className={styles.bodyTitle}>
          <td colSpan={hasAction ? columns.length + 1 : columns.length}>{title}</td>
        </tr>
      )}
      {data.map((row, rowIndex) => {
        const extraElement = extra?.(row);

        return (
          <React.Fragment key={rowIndex}>
            <tr
              className={classNames(rowClassName?.(row), { [styles.clickable]: isClickable })}
              {...(isClickable && {
                onClick: (e) => {
                  e.preventDefault();
                  onRowClick(row);
                },
                onKeyDown: (e) => {
                  if ((e.key === "Enter" || e.key === " ") && e.currentTarget === e.target) {
                    e.preventDefault();
                    onRowClick(row);
                  }
                },
                tabIndex: 0,
              })}
            >
              {columns.map((column, colIndex) => {
                let content: React.ReactNode;

                if (column.render) {
                  content = column.render(row[column.accessor], row, rowIndex);
                } else {
                  content = row[column.accessor] as React.ReactNode;
                }

                const cellTitle =
                  typeof content === "string" || typeof content === "number" ? content.toString() : undefined;

                return (
                  <td
                    key={`${rowIndex}${colIndex}`}
                    align={column.align || "left"}
                    className={classNames({
                      [styles.hasExtra]: Boolean(extraElement),
                      [styles.ellipsis]: column.ellipsis,
                    })}
                    style={{ width: column.width }}
                    aria-label={cellTitle}
                  >
                    {content}
                  </td>
                );
              })}
              {hasAction && (
                <td
                  align="right"
                  headers="action"
                  className={classNames(styles.action, { [styles.hasExtra]: Boolean(extraElement) })}
                >
                  {action?.(row, rowIndex)}
                </td>
              )}
            </tr>
            {extraElement && (
              <tr className={styles.extra}>
                <td colSpan={columns.length}>{extraElement}</td>
                {hasAction && <td />}
              </tr>
            )}
          </React.Fragment>
        );
      })}
    </tbody>
  );
};
